import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Loader2, ArrowRight, Trophy, Shield } from 'lucide-react';
import { LOGO_URL, BrandText } from '@/lib/brand';

export default function VerifyAcademy() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(null);
  const [countdown, setCountdown] = useState(5);

  // Phase 323 — Detect if this is a new JWT magic link so we DON'T
  // clear the auth we're about to stash post-verify. Legacy UUID
  // verifications still get the stale-auth wipe.
  const isJwtToken = !!token && token.split('.').length === 3;

  // Clear any stale auth on this page (legacy path only)
  useEffect(() => {
    if (isJwtToken) return;
    localStorage.removeItem('outplay_token');
    localStorage.removeItem('outplay_user');
  }, [isJwtToken]);

  useEffect(() => {
    if (!token) { setStatus('no_token'); return; }
    const verify = async () => {
      // Phase 323 — JWTs contain dots ("."). UUID-style legacy tokens
      // are pure hex. Route to the right endpoint based on shape so we
      // support BOTH the new gate AND any legacy /verify-academy emails
      // still in flight.
      const isJwt = token.split('.').length === 3;
      try {
        if (isJwt) {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });
          const d = await res.json();
          if (res.ok && d?.ok) {
            // The user is now a real DB row. Stash the auth token + the
            // temp password so the FirstLoginResetModal can immediately
            // walk them through setting their real password.
            try {
              localStorage.setItem('outplay_token', d.access_token);
              const u = { ...(d.user || {}), password_must_reset: true };
              localStorage.setItem('outplay_user', JSON.stringify(u));
              sessionStorage.setItem('outplay_temp_pw', d.temp_password || '');
              sessionStorage.setItem('outplay_first_login', '1');
            } catch (_) { /* ignore storage errors */ }
            setData({ academy_name: d?.user?.name || 'Your academy' });
            setStatus('success');
          } else {
            const code = d?.detail?.error || d?.detail || 'verify_failed';
            const friendly = (
              code === 'token_expired'
                ? 'This link has expired. Please request a new one from the signup page.'
                : code === 'email_already_registered'
                  ? 'You\'re already verified — please sign in.'
                  : 'The verification link is invalid or has expired.'
            );
            setData({ detail: friendly });
            setStatus('error');
          }
        } else {
          // Legacy path — UUID-style token → /api/verify-academy
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verify-academy?token=${token}`);
          const d = await res.json();
          if (res.ok) {
            setData(d);
            setStatus(d.status === 'already_verified' ? 'already' : 'success');
          } else {
            setData(d);
            setStatus('error');
          }
        }
      } catch (err) { setStatus('error'); }
    };
    verify();
  }, [token]);

  // Auto-redirect after successful verification. Phase 323 (JWT path)
  // lands the user in their dashboard already authenticated, where the
  // FirstLoginResetModal will demand they set their real password.
  // Legacy UUID path still goes to /login.
  useEffect(() => {
    if (status !== 'success' && status !== 'already') return;
    const target = isJwtToken && status === 'success' ? '/dashboard' : '/login';
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(target);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [status, navigate, isJwtToken]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="verify-page">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-background to-blue-50/30 dark:from-emerald-950/20 dark:via-background dark:to-blue-950/10" />
      <div className="relative w-full max-w-md text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <img src={LOGO_URL} alt="Out-Play" className="h-10 w-10 rounded-xl object-cover" />
          <BrandText className="text-2xl tracking-tight" />
        </Link>

        {status === 'loading' && (
          <Card className="border shadow-lg"><CardContent className="p-10">
            <Loader2 className="h-12 w-12 text-emerald-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-display font-bold">Verifying Your Academy...</h2>
            <p className="text-sm text-muted-foreground mt-2">Please wait while we confirm your email</p>
          </CardContent></Card>
        )}

        {status === 'success' && (
          <Card className="border-emerald-200 shadow-lg"><CardContent className="p-10">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-display font-bold text-emerald-700">Academy Verified!</h2>
            <p className="text-sm text-muted-foreground mt-2 mb-2">{data?.academy_name}</p>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 mb-4"><Trophy className="h-3 w-3 mr-1" /> 15-Day Free Trial Active</Badge>
            {!isJwtToken && (
              <p className="text-sm text-muted-foreground mb-2">Your trial ends on {data?.trial_end ? new Date(data.trial_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '15 days from now'}.</p>
            )}
            <p className="text-xs text-emerald-600 font-medium mb-4">
              {isJwtToken ? 'Set your password on the next screen and you\'re in.' : 'Check your inbox for a special welcome message from our CEO!'}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Redirecting to {isJwtToken ? 'your dashboard' : 'login'} in {countdown}s...
            </p>
            <Link to={isJwtToken ? '/dashboard' : '/login'}>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 h-11 text-base rounded-xl">
                {isJwtToken ? 'Enter My Workspace' : 'Login to Your Academy'} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent></Card>
        )}

        {status === 'already' && (
          <Card className="border-emerald-200 shadow-lg"><CardContent className="p-10">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-display font-bold text-emerald-700">Already Verified!</h2>
            <p className="text-sm text-muted-foreground mt-2 mb-2">{data?.academy_name || 'Your academy'} is already verified and active.</p>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 mb-4"><Trophy className="h-3 w-3 mr-1" /> Trial Active</Badge>
            <p className="text-xs text-muted-foreground mb-4">Redirecting to login in {countdown}s...</p>
            <Link to="/login"><Button className="w-full bg-emerald-600 hover:bg-emerald-700 h-11 text-base rounded-xl">Go to Login <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </CardContent></Card>
        )}

        {(status === 'error' || status === 'no_token') && (
          <Card className="border-red-200 shadow-lg"><CardContent className="p-10">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-display font-bold text-red-700">{status === 'no_token' ? 'Missing Token' : 'Verification Failed'}</h2>
            <p className="text-sm text-muted-foreground mt-2 mb-6">{status === 'no_token' ? 'No verification token provided. Please check your email for the verification link.' : (data?.detail || 'The verification link is invalid or has expired. If you already verified, please login directly.')}</p>
            <div className="flex flex-col gap-2">
              <Link to="/login"><Button className="w-full">Go to Login <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/signup"><Button variant="outline" className="w-full">Sign Up Again</Button></Link>
            </div>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
